import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { IStepperComponent, IStepperStep, IStepperStepState } from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import { BulkUploadContextService } from "@m19-board/services/bulk-upload/bulk-upload-context.service";

export type BulkUploadStepperStepId = "upload" | "preview" | "run";

const STEP_DEFS: { id: BulkUploadStepperStepId; labelKey: string }[] = [
  { id: "upload", labelKey: "bulk-upload-stepper.upload" },
  { id: "preview", labelKey: "bulk-upload-stepper.preview_import" },
  { id: "run", labelKey: "bulk-upload-stepper.import_results" },
];

@Component({
  selector: "app-bulk-upload-stepper",
  template: `<IStepper [steps]="steps()" clickable />`,
  imports: [IStepperComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUploadStepperComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly bulkUploadContext = inject(BulkUploadContextService);

  public readonly currentStep = input.required<BulkUploadStepperStepId>();

  private readonly previewBuckets = toSignal(this.bulkUploadContext.previewBuckets$, { initialValue: null });
  private readonly runResult = toSignal(this.bulkUploadContext.runResult$, { initialValue: null });

  protected readonly steps = computed<IStepperStep<BulkUploadStepperStepId>[]>(() => {
    const current = this.currentStep();
    const previewReached = this.previewBuckets() != null || current === "preview" || current === "run";
    const runReached = this.runResult() != null || current === "run";

    return STEP_DEFS.map((def) => ({
      id: def.id,
      label: this.transloco.translate(def.labelKey),
      state: resolveBulkUploadStepState(def.id, current, previewReached, runReached),
    }));
  });
}

function resolveBulkUploadStepState(
  id: BulkUploadStepperStepId,
  current: BulkUploadStepperStepId,
  previewReached: boolean,
  runReached: boolean,
): IStepperStepState {
  if (id === current) {
    return "current";
  }
  if (id === "upload") {
    return "default";
  }
  if (id === "preview") {
    return previewReached ? "default" : "disabled";
  }
  return runReached ? "default" : "disabled";
}
